// MIDAS V3 — Assistant message (Phase 2)
//
// Layout per spec §4 + handoff chat-window.jsx (lines 754-769):
//   • Header strip: 26×26 MidasLogoV3 + "Midas" Sora 600/13 + meta
//     `replied · HH:MM` mono 11 faint. NO "1 source" — that's a v2 detail.
//   • <ResponseCardV3> contains the rendered markdown body.
//   • Warning callout when message.srCitations resolves to an open SR.
//   • Follow-ups tray fed by message.suggestions (the existing v2 SSE field).
//
// Markdown rendering follows the v2 pattern (sentinels for <list-ref> tags
// preserved across markdown blocks so pipe tables still parse), but the
// component overrides are v3-themed: tokens reference var(--ink),
// var(--accent), var(--card-2), etc.; no Tailwind utility classes,
// no MUI. The `<list-ref>` chips render as `<CiteV3>` so a click opens
// the artifact dock (v2 dock in Phase 2; v3 dock in Phase 3).
//
// 2026-05-22: the markdown preprocessing helpers and the component renderer
// factory previously inlined here were lifted to ./markdownComponentsV3 so
// the new StreamingMarkdownV3 component can call the SAME renderers. This
// keeps the streaming surface and the final-message surface visually
// identical — without the shared factory, the prose would visually pop
// the moment streaming ended and the final message rendered with different
// overrides. No logic changes; the lift is purely structural.
//
// What we DO NOT port from v2:
//   • SR citation chip resolution flow (Phase 6 reskins MessageActions row).
//   • Source thumbnail strip / lightbox (Phase 6 reskins).
//   • Action card forms / context switch chips (those are platform features
//     of the bot SSE — they will render in a follow-up phase when the
//     v3 surface needs them).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  ChatArtifact,
  ChatMessage,
  DiagramOverlayArtifactData,
  MachineEvidenceArtifactData,
} from "../../../../shared/chatBot/models/chatBotModels";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { openArtifact, openArtifactAtPage } from "../../midasPageSlice";
// ME-MACHINEID-RENDER SP-3: deep-link target for a machine_evidence card into a
// machine_card tab (target "arm" = merged Picker+Jaws for MASH, §7 Q1 / SP-0).
import { MACHINE_CARD_ARM_TAB_SLUG } from "./artifacts/machineCardConstants";
import { setArtifactSourceThread } from "../../midasAttachSlice";
import { useAttachColumn } from "./attach/AttachColumnContext";
import {
  sendMessage,
  clearError,
} from "../../../../shared/chatBot/chatBotSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";
import ResponseCardV3, {
  ResponseCardFollowUp,
  ResponseCardSource,
  ResponseCardWarning,
} from "./ResponseCardV3";
import MessageActionsV3 from "./MessageActionsV3";
// MODEL-PICKER-11 (§14.2/§14.3): thinking transcript panel (collapsed,
// re-expandable on the completed turn) + per-turn stats row. Both `.cc`-gated
// with the model picker (isCcDevSurface).
import ThinkingPanelV3 from "./ThinkingPanelV3";
import TurnStatsRowV3 from "./TurnStatsRowV3";
import { isCcDevSurface } from "./ccSurface";
// R2 sub-task 7: Wire actionRequest to render the action card and
// auto-open the action_form artifact in the dock — same flow v2's
// ChatBotMessageV2 has had. MidasActionCardWrapper lives under
// shared/chatBot/components/ (NOT v2-private) so importing it from v3 is
// allowed per the spec's constraint check.
import MidasActionCardWrapper from "../../../../shared/chatBot/components/MidasActionCardWrapper";
// Markdown helpers + renderers lifted from this file (2026-05-22). Both
// this component (final-message render) and StreamingMarkdownV3 (in-stream
// render) import from here so the two surfaces share identical overrides.
import {
  buildMarkdownComponents,
  preprocessMarkdown,
  rewriteAllRefsToSentinels,
} from "./markdownComponentsV3";
// V3-SEGMENT-STREAMING (SP-1): split the stored/reloaded message body into an
// ordered block list (prose text blocks + marker rows) by WIRE ORDER, then
// render each block as a sibling in a single continuous column. Because
// stored/reloaded messages take THIS exact render path, marker-aware block
// rendering here covers the "reload/export mirrors the live view" requirement
// for free (the stored text contains the marker lines; loop_progress frames are
// transient and never persisted, by design).
import { splitMarkerBlocks } from "./markerBlocksV3";
import StepRowV3 from "./StepRowV3";

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

export interface ChatBotMessageV3Props {
  message: ChatMessage;
  /** Show the assistant header strip (only on the first message of a group). */
  showAvatar?: boolean;
}

const formatTime = (ts: string) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// FIX 1/2/3 (2026-06-20): Recover the page number for an artifact. For the
// documented Sinumerik alarm artifact (`type: "image"`, `source: "bot"`), the
// page lives ONLY in the url (`/page_156.jpg`) or title ("Page 156") — the
// numeric `page`/`data.page` fields are undefined. Recover in order:
//   1. a.page when it's a finite number,
//   2. parse /page_(\d+)/ from the url,
//   3. parse /(\d+)/ from the title.
// Returns undefined when no page can be recovered. Factored out (DRY) so
// handleCiteClick (FIX 1), the source-card mapper (FIX 2), and the active-chip
// highlight (FIX 3) all recover the page identically. `any` because the call
// sites pass either a ChatMessage artifact or an artifactTab artifact.
const recoverArtifactPage = (a: any): number | undefined => {
  if (!a) return undefined;
  if (typeof a.page === "number" && Number.isFinite(a.page)) return a.page;
  const urlMatch = a.url ? String(a.url).match(/page_(\d+)/) : null;
  if (urlMatch) return Number(urlMatch[1]);
  const titleMatch = a.title ? String(a.title).match(/(\d+)/) : null;
  if (titleMatch) return Number(titleMatch[1]);
  return undefined;
};

const ChatBotMessageV3: React.FC<ChatBotMessageV3Props> = ({
  message,
  showAvatar = true,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  // MODEL-PICKER P3: null = primary chat; non-null = this message renders
  // inside an attached column → follow-ups / regenerate / artifact opens
  // target the COLUMN's thread, not the global activeThreadId.
  const attachCtx = useAttachColumn();

  // Active artifact page (so the matching <Cite> chip can highlight).
  const artifactTabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeArtifactTabId = useAppSelector(
    (s) => s.midasPage.activeArtifactTabId
  );
  const activeRefPage = useMemo<number | null>(() => {
    const active = artifactTabs.find((t) => t.id === activeArtifactTabId);
    if (!active) return null;
    const artifact = active.artifact;
    if (artifact.source === "cross_reference") {
      const data = (artifact.data || {}) as DiagramOverlayArtifactData;
      return typeof data.page === "number" ? data.page : artifact.page ?? null;
    }
    // FIX 3 (2026-06-20): a successfully opened alarm image is `type: "image"`
    // (source "bot"), NOT cross_reference, so the OLD early-return left its
    // page-ref chip un-highlighted even while its page was open in the dock.
    // Match it by recovered page (consistent with FIX 1/2). FIX 1 now stamps
    // the recovered page onto artifact.page when the tab is opened, so
    // recoverArtifactPage usually resolves via a.page; the url/title fallback
    // covers any tab opened before that stamp.
    if (artifact.type === "image") {
      return recoverArtifactPage(artifact) ?? null;
    }
    return null;
  }, [artifactTabs, activeArtifactTabId]);

  const resolveActive = useCallback(
    (page: number) => activeRefPage === page,
    [activeRefPage]
  );

  // Open the matching cross_reference artifact (when present) when a Cite is
  // clicked. Logic ported from v2 ChatBotMessageV2.handleListRefClick.
  const handleCiteClick = useCallback(
    (page: number) => {
      // MODEL-PICKER P4 (artifact choreography): record which column this
      // artifact open came from — while the pane is open only the SOURCE
      // column stays visible (null = primary). Dispatched alongside every
      // openArtifact/openArtifactAtPage from a message subtree.
      dispatch(setArtifactSourceThread(attachCtx?.threadId ?? null));
      dispatch(openArtifactAtPage({ page }));
      const artifacts = message.artifacts || [];
      // Preferred: the bot emitted a cross_reference artifact for this page
      // (the <list-ref> / diagram-overlay path). Match it first.
      const crossRef = artifacts.find((a) => {
        if (a.source !== "cross_reference") return false;
        const data = (a.data || {}) as DiagramOverlayArtifactData;
        const dataPage = typeof data.page === "number" ? data.page : a.page;
        return dataPage === page;
      });
      // Fallback (MIDAS V3 alarm citations, 2026-06-20): <page-ref> alarm pages
      // arrive as a plain `type: "image"` artifact with `source: "bot"` — the
      // page number lives only in the url (`/page_156.jpg`) or title ("Page 156"),
      // NOT in a numeric `page`/`data.page`. recoverArtifactPage() does the
      // url/title parse (DRY — shared with the source-card path + highlight).
      const imageRef = artifacts.find((a) => {
        if (a.type !== "image") return false;
        return recoverArtifactPage(a) === page;
      });
      const matching = crossRef ?? imageRef;
      if (matching) {
        if (matching.url && !matching.url.startsWith("/") && matching.url !== "")
          return;
        // FIX 1 (2026-06-20): the dispatched artifact must carry the REAL page.
        // For the alarm image, matching.page is undefined (page lives in url/
        // title), so recover it once and pass the recovered number — the store
        // tab needs it for dedupe-by-(url,page) and the active-chip highlight.
        // The cross_reference branch already has matching.page populated, so
        // recoverArtifactPage falls straight through to a.page there (no
        // regression). When nothing can be recovered we fall back to the click
        // `page` (the chip the user actually clicked).
        const recoveredPage = recoverArtifactPage(matching) ?? page;
        dispatch(
          openArtifact({
            type: matching.type as any,
            title: matching.title,
            source: matching.source,
            url: matching.url || "",
            document_id: matching.document_id,
            page: recoveredPage,
            mime_type: matching.mime_type,
            entity_type: matching.entity_type,
            entity_id: matching.entity_id,
            file_id: matching.file_id,
            data: matching.data as any,
          })
        );
      }
    },
    [dispatch, message.artifacts, attachCtx]
  );

  // Wire CiteV3 dispatches through the same path as the markdown-rendered
  // <Cite> chips. We attach a click listener at the card container so we can
  // also handle the cross_reference auto-open, not just openArtifactAtPage.
  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      const btn = target?.closest("button.midas-v3-cite") as HTMLButtonElement | null;
      if (!btn) return;
      const pageAttr = btn.getAttribute("data-cite-page");
      const page = pageAttr ? Number(pageAttr) : NaN;
      if (Number.isFinite(page) && page > 0) {
        // CiteV3 has already dispatched openArtifactAtPage. We layer the
        // cross_reference-artifact resolver on top.
        handleCiteClick(page);
      }
    },
    [handleCiteClick]
  );

  // Memoise the markdown component factory.
  const markdownComponents = useMemo(
    () => buildMarkdownComponents(resolveActive),
    [resolveActive]
  );

  // V3-SEGMENT-STREAMING (SP-1): split the body into ordered blocks by wire
  // order (text vs marker), then pre-rewrite + preprocess EACH text block. The
  // marker rows carry position; text blocks render as independent ReactMarkdown
  // siblings. Per D1 (LOCKED): ONE continuous interleaved flow — rows and text
  // blocks are siblings in a single column, NO per-segment cards. (The chrome-
  // less look around this flow is SP-1V's job — do not restyle here.)
  //
  // MIDAS-LIVE-REF (2026-05-28): rewriteAllRefsToSentinels chains live-refs →
  // val → list-refs so <live-ref>, <val>, and <list-ref> tags all survive
  // markdown-table cells via the sentinel mechanism; preprocessMarkdown handles
  // bold-header → list restitching. Marker content is NEVER passed through
  // either — it renders as plain text via StepRowV3.
  const renderBlocks = useMemo(
    () =>
      splitMarkerBlocks(message.text || "").map((b) =>
        b.kind === "marker"
          ? b
          : {
              ...b,
              processed: preprocessMarkdown(rewriteAllRefsToSentinels(b.text)),
            }
      ),
    [message.text]
  );

  // Read context needed to dispatch a follow-up send. Selectors live here at
  // the component scope so the useMemo for followUps re-runs when any of them
  // changes (notably the streaming flag — we disable clicks during a stream
  // to avoid queueing a second message mid-stream).
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  // Column context wins over the global thread for send targeting +
  // streaming-disable (per-column independence).
  const activeThreadIsStreaming = useAppSelector((s) => {
    const tid = attachCtx ? attachCtx.threadId : s.midasPage.activeThreadId;
    if (!tid) return s.chatBot.isLoading;
    return s.chatBot.threadBuffers[tid]?.isLoading || false;
  });
  const targetThreadId = attachCtx ? attachCtx.threadId : activeThreadId;
  const targetMachineId = attachCtx
    ? attachCtx.machineId || undefined
    : machineContext?.machineId;

  // Optional follow-ups tray. Maps from `message.suggestions` (the existing
  // bot SSE field that v2 already consumes in ChatSuggestionChips).
  //
  // P6: chips now dispatch `sendMessage` with the chip text as the user
  // prompt — same arg shape ChatInputAreaV3 uses. Disabled while the active
  // thread is streaming so we don't queue a second message mid-stream.
  // Mirrors v2's `ChatPanel.handleSuggestionClick` (text → handleSend).
  const followUps: ResponseCardFollowUp[] | undefined = useMemo(() => {
    if (!message.suggestions || message.suggestions.length === 0) return undefined;
    return message.suggestions.map((label) => ({
      label,
      onClick: activeThreadIsStreaming
        ? undefined
        : () => {
            dispatch(clearError());
            dispatch(
              sendMessage({
                message: label,
                machineId: targetMachineId,
                threadId: targetThreadId || undefined,
                originThreadId: targetThreadId || undefined,
              })
            );
          },
    }));
  }, [
    message.suggestions,
    dispatch,
    targetMachineId,
    targetThreadId,
    activeThreadIsStreaming,
  ]);

  // Artifact-thumbnail strip — V2 Path B parity (2026-05-21 follow-up).
  //
  // V2 has TWO distinct source/artifact surfaces under each assistant message:
  //
  //   Path A — "Source cards" from `message.sources[]` (V2 line 862).
  //     Hidden on MIDAS via `{showSources && !isOnMidasPage && ...}`. These
  //     are bot-emitted text/image sources. The previous 2026-05-21 fix
  //     correctly identified that V1 and V2 both suppress this row on the
  //     MIDAS surface — there is nothing for V3 to render from this feed.
  //
  //   Path B — "Artifact thumbnails" from `message.artifacts[]` (V2 line
  //     826-834). These ARE rendered on MIDAS via `allArtifacts = isOnMidas
  //     Page ? message.artifacts || [] : richArtifacts;` (V2 line 739) and
  //     are shown as a row of `<MidasArtifactThumbnail>` (`.slice(0, 5)`).
  //     URL resolution for the underlying preview lives in
  //     `MidasArtifactThumbnail.tsx:99-145`: diagram_overlay artifacts hit
  //     `/v1/bot/diagram/{page}/image`, image/platform_file artifacts hit
  //     `/v1/bot/image/{path}` via the standard proxy resolver.
  //
  // The previous fix (commit 1549a1d1) hard-coded `sources` to undefined on
  // V3, which removed Path A correctly but also starved the V3 source-card
  // infrastructure (`ResponseCardV3 -> ResponseSourceCardV3`) of Path B
  // data. The user-visible regression: the row of 5 electrical-diagram page
  // squares disappeared from the bottom of `/midas-v2` (V3) responses.
  //
  // This memo restores Path B by mapping `message.artifacts[]` (filtered to
  // image-bearing types diagram_overlay/image/platform_file, capped at 5
  // for V2 parity) into `ResponseCardSource[]`. We build the absolute proxy
  // URL inline (the same URL `MidasArtifactThumbnail.getImageUrlToFetch`
  // produces) and pass it through `ResponseSourceCardV3` — its proxy
  // resolver short-circuits absolute URLs verbatim (see the 2026-05-21
  // addition to that file). `handleCiteClick` continues to handle the cross-
  // reference auto-open flow for inline <CiteV3> chips in the prose, which
  // is a separate cross-ref UI and stays intact.
  const apiBaseUrl: string =
    (window as any).app?.config?.apiBaseUrl || "";

  const sources: ResponseCardSource[] | undefined = useMemo(() => {
    const all = message.artifacts || [];
    if (all.length === 0) return undefined;

    const imageBearing = all.filter(
      (a) =>
        a.type === "diagram_overlay" ||
        a.type === "image" ||
        a.type === "platform_file"
    );
    if (imageBearing.length === 0) return undefined;

    const top5 = imageBearing.slice(0, 5);

    const mapped: ResponseCardSource[] = top5.map((a) => {
      // Canonical join key — same precedence as ChatBotMessageV2
      // (data.page when available, fall back to artifact.page).
      // FIX 2 (2026-06-20): for an alarm image (`type: "image"`, source "bot")
      // both data.page and a.page are undefined, so the OLD `?? 0` produced
      // page=0 → label "File" → handleCiteClick(0) never matched. Recover the
      // page from the url/title (same recoverArtifactPage helper used by FIX 1)
      // so the card shows "Page 156" and clicks through to the right page.
      const data = (a.data || {}) as DiagramOverlayArtifactData;
      const dataPage = typeof data.page === "number" ? data.page : undefined;
      const page = dataPage ?? recoverArtifactPage(a) ?? 0;

      // Build absolute proxy URL for the preview thumbnail.
      // Mirrors MidasArtifactThumbnail.getImageUrlToFetch + fetchImage:
      //   diagram_overlay -> /v1/bot/diagram/{page}/image
      //   image/platform_file -> /v1/bot/image/{stripped path} via the
      //     existing resolver inside ResponseSourceCardV3 (we pass the
      //     relative artifact.url through and let it resolve normally).
      let imageUrl: string | undefined;
      if (a.type === "diagram_overlay") {
        if (page > 0) {
          imageUrl = `${apiBaseUrl}/v1/bot/diagram/${page}/image`;
        }
      } else if (a.type === "image" || a.type === "platform_file") {
        // SEC-REQ-5: only forward relative URLs. ResponseSourceCardV3's
        // resolver then rewrites them onto the /v1/bot/image/{path} proxy.
        if (a.url && a.url.startsWith("/")) {
          imageUrl = a.url;
        }
      }

      // Label = compact mono pill.
      //   diagram_overlay -> `List ${list_id}` when bot provided one,
      //     otherwise `Page ${page}` (V2's MidasArtifactThumbnail title
      //     strip just shows artifact.title, but ResponseSourceCardV3's
      //     pill slot needs something compact; the title goes into `name`).
      //   image/platform_file -> `Page ${page}` when available, else "File".
      let label: string;
      if (a.type === "diagram_overlay") {
        const listId = (data as any)?.list_id as string | undefined;
        label = listId ? `List ${listId}` : page > 0 ? `Page ${page}` : "Diagram";
      } else {
        label = page > 0 ? `Page ${page}` : "File";
      }

      return {
        label,
        page,
        name: a.title,
        imageUrl,
        // Reuse the cross-reference click handler — when the page matches
        // a cross_reference artifact it auto-opens that artifact in the
        // dock; otherwise it just dispatches openArtifactAtPage. For non-
        // cross-reference artifacts (the common case here), we also want
        // to open the artifact itself in the dock. handleCiteClick already
        // does both: openArtifactAtPage first, then the cross-ref resolver
        // layered on top. Direct-open of non-cross-ref artifacts happens
        // through openArtifactAtPage's existing logic.
        onClick: (p: number) => handleCiteClick(p),
      };
    });

    return mapped;
  }, [message.artifacts, apiBaseUrl, handleCiteClick]);

  // ME-MACHINEID-RENDER SP-3: chat affordance to open a machine_card /
  // machine_evidence artifact in the dock. The v3 `sources` memo above only
  // surfaces image-bearing artifacts (diagram_overlay/image/platform_file), so
  // pre-SP-3 nothing opened these evidence cards. Emit is OFF this milestone —
  // these arrays are empty in practice; wiring is exercised via SP-6 fixtures.
  const evidenceAffordances: ChatArtifact[] = useMemo(() => {
    const all = message.artifacts || [];
    return all.filter(
      (a) => a.type === "machine_card" || a.type === "machine_evidence"
    );
  }, [message.artifacts]);

  const handleOpenEvidence = useCallback(
    (artifact: ChatArtifact) => {
      // MODEL-PICKER P4: record the source column (null = primary) like the
      // Cite/source-card opens do.
      dispatch(setArtifactSourceThread(attachCtx?.threadId ?? null));
      const artifacts = message.artifacts || [];

      // A machine_evidence card deep-links into a machine_card TAB
      // (links_to.machine_card_tab; target "arm" for MASH). When the reply also
      // carries the sibling machine_card, open THAT on the deep-link tab so the
      // affordance honors the link. The SP-4 machine_evidence viewer (separate
      // sub-phase) renders the card itself; here we prefer the richer card.
      if (artifact.type === "machine_evidence") {
        const evData = (artifact.data || {}) as MachineEvidenceArtifactData;
        const targetTab =
          evData.links_to?.machine_card_tab || MACHINE_CARD_ARM_TAB_SLUG;
        const sibling = artifacts.find((a) => a.type === "machine_card");
        if (sibling) {
          dispatch(
            openArtifact({
              type: "machine_card" as any,
              title: sibling.title,
              source: sibling.source,
              url: sibling.url || "",
              page: sibling.page,
              // Client render hint (not in ME-CONTRACT): open on the deep-link tab.
              data: { ...(sibling.data as any), _deepLinkTabSlug: targetTab },
            })
          );
          return;
        }
      }

      dispatch(
        openArtifact({
          type: artifact.type as any,
          title: artifact.title,
          source: artifact.source,
          url: artifact.url || "",
          document_id: artifact.document_id,
          page: artifact.page,
          mime_type: artifact.mime_type,
          entity_type: artifact.entity_type,
          entity_id: artifact.entity_id,
          data: artifact.data as any,
        })
      );
    },
    [dispatch, message.artifacts, attachCtx]
  );

  // Optional warning callout. The simplest signal we have right now is the
  // presence of srCitations — the bot emits these when the response context
  // touched an open service report on the active subsystem. Real "is open"
  // resolution lives in the v2 SR chip click flow; for Phase 2 we surface
  // the first citation's number as a callout subtitle if present.
  const warning: ResponseCardWarning | undefined = useMemo(() => {
    const first = (message.srCitations || [])[0];
    if (!first) return undefined;
    return {
      title: translate("midas.v3.responseCard.openSrTitle", {
        defaultValue: "Open service report on this subsystem",
      }) as string,
      subtitle: first.reportNumber,
      // Click handler intentionally left unset in Phase 2 — Phase 6
      // restores the full SR resolution flow.
    };
  }, [message.srCitations, translate]);

  // Title row of the response card. Phase 2 uses a translation-keyed fallback
  // because the bot SSE doesn't currently emit a card title; v2's
  // `ChatBotMessageV2` shows "MIDAS · replied" via a header strip outside the
  // card. The v3 card title doubles as that piece of information.
  const cardTitle = translate("midas.v3.responseCard.defaultTitle", {
    defaultValue: "MIDAS",
  }) as string;

  return (
    <div
      style={{
        marginBottom: 22,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {showAvatar && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <MidasLogoV3 size={26} />
          <span
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "var(--ink)",
              fontFamily: "var(--font-sans)",
            }}
          >
            Midas
          </span>
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: "var(--faint)",
              letterSpacing: "0.04em",
            }}
          >
            {translate("midas.v3.assistant.replied", {
              defaultValue: "replied",
            }) as string}{" "}
            · {formatTime(message.timestamp)}
          </span>
        </div>
      )}

      {/* MODEL-PICKER-11 (§14.2): completed-turn thinking panel — collapsed
          by default, re-expandable while the turn is on screen. Rendered
          ABOVE the answer card. Absent on non-thinking turns. */}
      {isCcDevSurface() && message.thinkingText && (
        <ThinkingPanelV3
          text={message.thinkingText}
          streaming={false}
          autoExpanded={false}
        />
      )}

      <div onClickCapture={handleCardClick}>
        {/* SP-1V (Creative Designer): `flat` renders the assistant turn chrome-
            less (no card fill/border/radius/shadow/title) so it flows directly
            on the page background per the §1A reference images — and so there
            is no visual pop when the flat StreamingMarkdownV3 surface settles
            into this final message. Scoped here (the v3 assistant turn is the
            ONLY ResponseCardV3 consumer); the carded default is untouched. */}
        <ResponseCardV3
          flat
          title={cardTitle}
          warning={warning}
          sources={sources}
          followUps={followUps}
        >
          {/* SP-1: ordered blocks — marker rows + prose siblings in wire order. */}
          {renderBlocks.map((b, i) =>
            b.kind === "marker" ? (
              <StepRowV3 key={`blk-${i}`} label={b.label} />
            ) : (
              <ReactMarkdown
                key={`blk-${i}`}
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {b.processed}
              </ReactMarkdown>
            )
          )}
        </ResponseCardV3>
      </div>

      {/* ME-MACHINEID-RENDER SP-3: machine_card / machine_evidence affordance —
          a chip under the reply that opens the evidence card in the dock. For a
          machine_evidence card it honors links_to.machine_card_tab (→ "arm").
          Empty in practice this milestone (emit gate OFF). */}
      {evidenceAffordances.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {evidenceAffordances.map((a, i) => (
            <button
              key={`evidence-${a.type}-${i}`}
              type="button"
              className="midas-v3-evidence-affordance"
              onClick={() => handleOpenEvidence(a)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 28,
                padding: "0 12px",
                fontSize: 12,
                fontFamily: "var(--font-sans)",
                background: "var(--card)",
                color: "var(--ink-2)",
                border: "1px solid var(--border)",
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              <span aria-hidden style={{ color: "var(--accent)" }}>▦</span>
              <span
                style={{
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {a.title ||
                  (a.type === "machine_evidence"
                    ? (translate("midas.v3.evidence.openEvidence", {
                        defaultValue: "Machine evidence",
                      }) as string)
                    : (translate("midas.v3.evidence.openCard", {
                        defaultValue: "Machine card",
                      }) as string))}
              </span>
              <span aria-hidden style={{ color: "var(--faint)" }}>↗</span>
            </button>
          ))}
        </div>
      )}

      {/* MODEL-PICKER-11 (§14.3): per-turn stats row under the completed
          answer. Only mounts when the turn carried an `event: stats` frame —
          hard-error turns and un-honored picks have none (no placeholder).
          Refusal turns DO carry stats and render the refusal flag. */}
      {isCcDevSurface() && message.modelStats && (
        <TurnStatsRowV3 stats={message.modelStats} />
      )}

      {/* R2 sub-task 7: action card + auto-open of action_form artifact.
          Mirrors ChatBotMessageV2.tsx line 999 exactly. The wrapper handles:
            • Permission gating (useActionExecutor.canExecute)
            • Stale timeout (30s polling)
            • Auto-dispatch openArtifact({ type: "action_form", ... }) on mount
              — the v3 dock's MidasArtifactPaneV3 already wraps action_form via
              MidasArtifactActionFormV2 (P3, lines 14, 58-59, 167, 187).
            • Confirm / cancel / retry / dismiss flows.
          Visual styling lives in the wrapper itself (shared infrastructure
          used by both v1 and v2). Behaviour parity > theme parity for now;
          a future cycle can wrap or re-skin if v3 visual divergence matters. */}
      {message.actionRequest && (
        <MidasActionCardWrapper actionRequest={message.actionRequest} />
      )}

      {/* P6: Message-actions row under each assistant response. We render
          unconditionally on assistant messages — the parent (ChatMessageListV3)
          only mounts this component for non-streaming messages, so we don't
          need a status guard here.
          R5 sub-task 5a: forward `botMessageId` (bot-minted, captured from SSE
          answer/done in streamBotMessage.ts) + `threadId` so Like/Dislike can
          POST to /v1/bot/midas/feedback. */}
      <MessageActionsV3
        messageId={message.id}
        text={message.text || ""}
        botMessageId={message.botMessageId}
        threadId={targetThreadId}
      />
    </div>
  );
};

export default ChatBotMessageV3;
